import React, { useCallback, useRef, useState } from 'react';

import InputTextField from '../../../inputfields/InputTextField';
import TemperatureField from 'components/inputfields/TemperatureField';

import IProduct from '../../../../models/product';
import { isBlank } from 'utils/helpers-ts';
import ThreeWaySwitch from 'components/inputfields/ThreeWaySwitch';
import { getProductFieldVisibility } from '../utils/productFieldVisibility';

interface ITemperatureProps {
  readOnly?: boolean;
  state: IProduct;
  setState: React.Dispatch<React.SetStateAction<IProduct>>;
  approve?: boolean;
}

export const Temperature = ({
  readOnly,
  state,
  setState,
  approve,
}: ITemperatureProps) => {
  const isNotStore = !state?.producerIsStore;
  const { sections } = getProductFieldVisibility({ product: state });
  const [shouldAutoPopulate, setShouldAutoPopulate] = useState(false);

  const hasMaxTemp = state?.max_temperature !== undefined && state?.max_temperature !== null;

  const [isMinTransportValueChanged, setIsMinTransportValueChanged] = useState(false);
  const [isMaxTransportValueChanged, setIsMaxTransportValueChanged] = useState(false);

  const keepRef = useRef({ maxTeperature: state.max_temperature });

  // TODO any
  const handleChange = ({ target: { name, value } }: any) => {
    if (state) {
      setShouldAutoPopulate(false);
      if (name === 'trade_item_temperature_information') {
        setState({ ...state, [name]: value });
      }
    }
  };

  React.useEffect(() => {
    setShouldAutoPopulate(keepRef.current.maxTeperature !== state.max_temperature);
    keepRef.current.maxTeperature = state.max_temperature;
  }, [state.max_temperature]);

  React.useEffect(() => {
    if (shouldAutoPopulate && hasMaxTemp) {
      const text = state.max_temperature && state.max_temperature > 10
        ?'Förvaras i rumstemperatur'
        : `Förvaras vid högst ${state.max_temperature && state.max_temperature >= 0 ? '+' : ''}${state.max_temperature}°C`;
      setState((oldState) => {
        const oldValue = oldState.trade_item_temperature_information;
        if (isBlank(oldValue)) {
          return {...oldState, trade_item_temperature_information: text};
        }
        if (
          oldValue?.trim()?.startsWith('Förvaras vid högst') ||
          oldValue?.trim() === 'Förvaras i rumstemperatur'
        ) {
          return {...oldState, trade_item_temperature_information: text};
        }
        return oldState;
      });
      setShouldAutoPopulate(false);
    }
  }, [shouldAutoPopulate, hasMaxTemp, state.max_temperature, setState]);

  const onTemperatureChange = (value: number, name: string): void => {
    setState({ ...state, [name]: value });
  };

  const onMinTemperatureChange = (value: number, name: string) => {
    if (setState) {
      if (isNotStore) {
        setState({...state, [name]: value, transportMinTemperature: isMinTransportValueChanged ? state?.transportMinTemperature : value});
      } else {
        setState({...state, [name]: value });
      }
    }
  };

  const onMaxTemperatureChange = (value: number, name: string) => {
    if (setState) {
      if (isNotStore) {
        setState({...state, [name]: value, transportMaxTemperature: isMaxTransportValueChanged ? state?.transportMaxTemperature : value});
      } else {
        setState({...state, [name]: value });
      }
    }
  };

  const setTempError = useCallback((value: boolean) => {
    setState && setState((oldState) => ({ ...oldState, showTempError: value }));
  }, [setState]);

  return (
    <>
      <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <TemperatureField
            readOnly={readOnly}
            inputLabelProps = {{
              heading: "Förvaringstemperatur",
              text: "Ange den lägsta och högsta temperaturen som produkten tål utan påverkan på produktsäkerhet eller kvalitet. Glöm inte att ange “-” eller “+” framför gradantalet.",
            }}
            minValueProps = {{
              inputName: "min_temperature",
              defaultValue: state?.min_temperature,
              value: state?.min_temperature,
              placeholder: "Ex. +0",
              onChange: onMinTemperatureChange,
            }}
            maxValueProps = {{
              inputName: "max_temperature",
              defaultValue: state?.max_temperature,
              value: state?.max_temperature,
              placeholder: "Ex. +10",
              onChange: onMaxTemperatureChange,
            }}
            setTempError={isNotStore ? setTempError : undefined}
          />
          {sections.transportTemperature && (
            <TemperatureField
              readOnly={readOnly}
              inputLabelProps = {{
                heading: "Transporttemperatur",
                text: "Ange den lägsta och högsta temperaturen som produkten tål under transport utan påverkan på produktsäkerhet eller kvalitet. Glöm inte att ange “-” eller “+” framför gradantalet.",
              }}
              minValueProps = {{
                inputName: "transportMinTemperature",
                value: state?.transportMinTemperature,
                placeholder: "Ex. +0",
                onChange: onTemperatureChange,
                setIsChanged: setIsMinTransportValueChanged,
              }}
              maxValueProps = {{
                inputName: "transportMaxTemperature",
                value: state?.transportMaxTemperature,
                placeholder: "Ex. +8",
                onChange: onTemperatureChange,
                setIsChanged: setIsMaxTransportValueChanged,
              }}
              setTempError={setTempError}
            />      
          )}
        </div>
        {approve && state?.approve?.find(x => x.field === 'temperatures') && (
          <ThreeWaySwitch
            readOnly={readOnly}
            state={state}
            setState={setState}
            name={"temperatures" as keyof IProduct}
          />
        )}
      </div>
      <InputTextField
        readOnly={readOnly}
        style={{ marginTop: 7 }}
        placeholder="Ex. Förvaras vid högst +4°C"
        multiline
        inputName="trade_item_temperature_information"
        onChange={handleChange}
        value={state?.trade_item_temperature_information}
        required
        setState={setState}
        approve={approve}
        state={state}
        multilineRows={1}
        multilineMax={4}
        withLabel
        labelOptions={{
          heading: 'Förvaringsanvisning',
          text: "Ange hur produkten ska förvaras hemma hos konsumenten. T.ex. 'Förvaras vid högst +4°C', 'Förvaras torrt och mörkt', 'Hållbar 3 dagar efter öppnad förpackning'. Obligatoriskt för produkter med sista förbrukningsdag.",
          link: 'https://kontrollwiki.livsmedelsverket.se/artikel/42/forvaringsanvisning',
        }}
      />
    </>
  );
};

export default Temperature;
